import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { API_URL, getAuthHeader, formatFileSize, getFileIcon } from '../utils/config';
import useStore from '../store/useStore';
import { getSocket } from '../hooks/useSocket';
import { encryptMessage, decryptMessage } from '../utils/encryption';
import { playNotifSound, unlockAudio } from '../utils/notifSound';

// ── Avatar ──────────────────────────────────────────────
const Avatar = ({ name, url, size = 10, online = false }) => {
  const isReal = url && url.startsWith('https://');
  return (
    <div className="relative flex-shrink-0">
      <div className="rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold overflow-hidden"
        style={{ width: `${size * 4}px`, height: `${size * 4}px` }}>
        {isReal
          ? <img src={`https://qtalk.qualtechedge.in${url}`} className="w-full h-full object-cover" alt="" />
          : <span style={{ fontSize: `${size * 1.6}px` }}>{(name?.[0] || '?').toUpperCase()}</span>}
      </div>
      {online && <span className="absolute bottom-0 right-0 bg-green-400 rounded-full border-2 border-white" style={{ width: '10px', height: '10px' }} />}
    </div>
  );
};

// ── Main ─────────────────────────────────────────────────
export default function MobileChatPage() {
  const { user, conversations, setConversations, updateConversation,
    activeConversation, setActiveConversation, onlineUsers, typingUsers, logout } = useStore();

  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [view, setView] = useState('chats'); // 'chats' | 'chat' | 'people' | 'profile'
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: '', mobile: '', department: '', designation: '', status: '' });
  const [editingProfile, setEditingProfile] = useState(false);
  const [swipeStartX, setSwipeStartX] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeConvRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const isOnline = (uid) => !!onlineUsers[uid] || !!onlineUsers[Number(uid)];

  useEffect(() => {
    fetchConversations();
    fetchUsers();
    fetchMyProfile();
    const unlock = () => { unlockAudio(); document.removeEventListener('click', unlock); };
    document.addEventListener('click', unlock);
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    activeConvRef.current = activeConversation;
    if (activeConversation) {
      fetchMessages(activeConversation.id);
      setUnreadCounts(prev => ({ ...prev, [activeConversation.id]: 0 }));
      const s = getSocket();
      if (s) { s.emit('join_conversation', activeConversation.id); s.emit('mark_read', { conversation_id: activeConversation.id }); }
    }
  }, [activeConversation]);

  // Socket listener
  useEffect(() => {
    let attempts = 0;
    const register = () => {
      const s = getSocket();
      if (!s) { if (attempts++ < 20) setTimeout(register, 300); return; }
      s.off('new_message', handleNewMessage);
      s.on('new_message', handleNewMessage);
    };
    const handleNewMessage = (msg) => {
      const isCurrentConv = activeConvRef.current && msg.conversation_id === activeConvRef.current.id;
      const isOwn = msg.sender_id === user?.id;
      if (isCurrentConv) {
        if (!isOwn) { const sound = localStorage.getItem('qtalk_setting_msgSound'); playNotifSound(sound ? JSON.parse(sound) : 'Default'); }
        setMessages(prev => {
          const safe = Array.isArray(prev) ? prev : [];
          if (safe.find(m => m.id === msg.id)) return safe;
          return [...safe, msg];
        });
      } else {
        if (!isOwn) { const sound = localStorage.getItem('qtalk_setting_msgSound'); playNotifSound(sound ? JSON.parse(sound) : 'Default'); }
        setUnreadCounts(prev => ({ ...prev, [msg.conversation_id]: (prev[msg.conversation_id] || 0) + 1 }));
        setNotifications(prev => [{ id: Date.now(), title: msg.sender_name, body: msg.message_type === 'text' ? decryptMessage(msg.content)?.substring(0, 60) : '📎 File', conversation_id: msg.conversation_id, time: new Date(), read: false }, ...prev.slice(0, 19)]);
        setUnreadNotifs(n => n + 1);
      }
      updateConversation({ id: msg.conversation_id, last_message: msg.content || msg.file_name, last_message_time: msg.created_at });
    };
    register();
    return () => { const s = getSocket(); if (s) s.off('new_message', handleNewMessage); };
  }, []);

  const fetchConversations = async () => {
    try { const { data } = await axios.get(`${API_URL}/chat/conversations`, { headers: getAuthHeader() }); setConversations(Array.isArray(data) ? data : []); } catch (e) { }
  };
  const fetchUsers = async () => {
    try { const { data } = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() }); setUsers(data); } catch (e) { }
  };
  const fetchMyProfile = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/users/me`, { headers: getAuthHeader() });
      setProfileForm({ full_name: data.full_name || '', mobile: data.mobile || '', department: data.department || '', designation: data.designation || '', status: data.status || '' });
    } catch (e) { }
  };
  const fetchMessages = async (convId) => {
    try {
      const { data } = await axios.get(`${API_URL}/chat/conversations/${convId}/messages`, { headers: getAuthHeader() });
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) { setMessages([]); }
  };

  const openConversation = (conv) => {
    setActiveConversation(conv);
    activeConvRef.current = conv;
    setView('chat');
  };

  const startChat = async (userId) => {
    try {
      const { data } = await axios.post(`${API_URL}/chat/conversations/direct`, { user_id: userId }, { headers: getAuthHeader() });
      await fetchConversations();
      const targetUser = users.find(u => u.id === userId);
      const conv = { id: data.conversation_id, type: 'direct', name: targetUser?.full_name, avatar_url: targetUser?.avatar_url, other_user: targetUser };
      setActiveConversation(conv);
      setView('chat');
    } catch (e) { toast.error('Failed to start chat'); }
  };

  const sendMessage = useCallback(() => {
    if (!messageInput.trim() || !activeConversation) return;
    const s = getSocket();
    if (!s) return toast.error('Not connected');
    s.emit('send_message', {
      conversation_id: activeConversation.id,
      content: encryptMessage(messageInput.trim()),
      message_type: 'text',
      reply_to_id: replyTo?.id || null
    });
    setMessageInput('');
    setReplyTo(null);
  }, [messageInput, activeConversation, replyTo]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleTyping = (e) => {
    setMessageInput(e.target.value);
    const s = getSocket();
    if (!s || !activeConversation) return;
    s.emit('typing_start', { conversation_id: activeConversation.id });
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => s.emit('typing_stop', { conversation_id: activeConversation.id }), 1500);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeConversation) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', activeConversation.id);
      const { data } = await axios.post(`${API_URL}/files/upload`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      const s = getSocket();
      if (s) s.emit('send_message', { conversation_id: activeConversation.id, message_type: file.type.startsWith('image/') ? 'image' : 'file', content: null, file_url: data.file_url, file_name: data.file_name, file_size: data.file_size, file_mime_type: data.file_mime_type });
      toast.success('File sent!');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const saveProfile = async () => {
    try {
      await axios.put(`${API_URL}/users/me`, profileForm, { headers: getAuthHeader() });
      toast.success('Profile updated!');
      setEditingProfile(false);
    } catch (e) { toast.error('Failed to update'); }
  };

  const getDateLabel = (date) => {
    if (isToday(new Date(date))) return 'Today';
    if (isYesterday(new Date(date))) return 'Yesterday';
    return format(new Date(date), 'MMM d, yyyy');
  };

  const typingInConv = activeConversation ? typingUsers[activeConversation.id] : null;
  const filteredConvs = conversations.filter(c => (c.name || '').toLowerCase().includes(search.toLowerCase()));
  const filteredUsers = users.filter(u => u.full_name.toLowerCase().includes(search.toLowerCase()));
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  // ── SWIPE to go back ──
  const handleTouchStart = (e) => setSwipeStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (swipeStartX !== null && e.changedTouches[0].clientX - swipeStartX > 80) {
      if (view === 'chat') { setView('chats'); setActiveConversation(null); }
      else if (view === 'people' || view === 'profile') setView('chats');
    }
    setSwipeStartX(null);
  };

  // ── RENDER ────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden"
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

      {/* ── CHAT LIST VIEW ── */}
      {view === 'chats' && (
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-4 pt-12 pb-3 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-xl font-black text-gray-900 dark:text-white">Messages</h1>
              <div className="flex items-center gap-2">
                <button onClick={() => { setShowNotifs(true); setUnreadNotifs(0); }}
                  className="relative w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
                  </svg>
                  {unreadNotifs > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />}
                </button>
                <button onClick={() => setView('newchat')}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
                  style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="relative">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-2.5 w-4 h-4 text-gray-400">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input type="text" placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none" />
            </div>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {filteredConvs.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12 opacity-30">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
                <p className="text-sm">No conversations yet</p>
                <button onClick={() => setView('newchat')} className="text-xs text-teal-500 font-semibold mt-1">Start a new chat →</button>
              </div>
            )}
            {filteredConvs.map(conv => (
              <div key={conv.id} onClick={() => openConversation(conv)}
                className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-50 dark:border-gray-800 active:bg-gray-100 dark:active:bg-gray-800 transition cursor-pointer">
                <Avatar name={conv.name} url={conv.avatar_url} size={12} online={conv.other_user && isOnline(conv.other_user.id)} />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{conv.name || 'Chat'}</p>
                    {conv.last_message_time && <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{format(new Date(conv.last_message_time), 'HH:mm')}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-xs text-gray-400 truncate flex-1">
                      {conv.last_message_type === 'image' ? '🖼️ Photo' : conv.last_message_type === 'file' ? '📎 File' : conv.last_message ? decryptMessage(conv.last_message) : 'No messages yet'}
                    </p>
                    {unreadCounts[conv.id] > 0 && (
                      <span className="ml-2 min-w-[18px] h-[18px] px-1 bg-teal-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {unreadCounts[conv.id] > 99 ? '99+' : unreadCounts[conv.id]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── NEW CHAT VIEW ── */}
      {view === 'newchat' && (
        <div className="flex flex-col h-full">
          <div className="px-4 pt-12 pb-3 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-3 mb-3">
              <button onClick={() => setView('chats')} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gray-600 dark:text-gray-300">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <h1 className="text-lg font-black text-gray-900 dark:text-white">New Conversation</h1>
            </div>
            <div className="relative">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-2.5 w-4 h-4 text-gray-400">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input autoFocus type="text" placeholder="Search people..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {/* Online first */}
            {filteredUsers.filter(u => isOnline(u.id)).length > 0 && (
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 py-2">Online</p>
            )}
            {filteredUsers.filter(u => isOnline(u.id)).map(u => (
              <div key={u.id} onClick={() => startChat(u.id)} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-800 active:bg-gray-100 cursor-pointer">
                <Avatar name={u.full_name} url={u.avatar_url} size={11} online={true} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{u.full_name}</p>
                  <p className="text-xs text-gray-400">{u.designation || u.department || u.email}</p>
                </div>
              </div>
            ))}
            {filteredUsers.filter(u => !isOnline(u.id)).length > 0 && (
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 py-2 mt-1">Others</p>
            )}
            {filteredUsers.filter(u => !isOnline(u.id)).map(u => (
              <div key={u.id} onClick={() => startChat(u.id)} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-800 active:bg-gray-100 cursor-pointer">
                <Avatar name={u.full_name} url={u.avatar_url} size={11} online={false} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{u.full_name}</p>
                  <p className="text-xs text-gray-400">{u.designation || u.department || u.email}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CHAT VIEW ── */}
      {view === 'chat' && activeConversation && (
        <div className="flex flex-col h-full">
          {/* Chat header */}
          <div className="px-4 pt-12 pb-3 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3">
            <button onClick={() => { setView('chats'); setActiveConversation(null); }}
              className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gray-600 dark:text-gray-300">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <Avatar name={activeConversation.name} url={activeConversation.avatar_url} size={9} online={activeConversation.other_user && isOnline(activeConversation.other_user.id)} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 dark:text-white text-sm truncate">{activeConversation.name}</p>
              <p className="text-xs text-gray-400">
                {typingInConv ? <span className="text-teal-500 animate-pulse">{typingInConv.name} is typing...</span>
                  : activeConversation.other_user && isOnline(activeConversation.other_user.id) ? <span className="text-green-500">● Online</span>
                  : 'Offline'}
              </p>
            </div>
            <span className="text-xs text-teal-500 flex items-center gap-1">🔒 E2E</span>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3" style={{ background: 'radial-gradient(ellipse at top, #f0fdf8 0%, #f8fafc 100%)' }}>
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <span className="text-4xl mb-2">👋</span>
                <p className="text-sm">Say hello!</p>
              </div>
            )}
            {messages.map((msg, idx) => {
              const isMe = Number(msg.sender_id) === Number(user?.id);
              const prevMsg = messages[idx - 1];
              const showDate = !prevMsg || !isSameDay(new Date(msg.created_at), new Date(prevMsg.created_at));
              return (
                <div key={msg.id}>
                  {showDate && (
                    <div className="flex items-center gap-2 my-3">
                      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                      <span className="text-xs text-gray-400 bg-white dark:bg-gray-900 px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700">{getDateLabel(msg.created_at)}</span>
                      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                    </div>
                  )}
                  {/* Reply preview */}
                  <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-1`}>
                    <div className={`max-w-[78%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                      {!isMe && <span className="text-xs text-teal-600 font-semibold mb-0.5 px-1">{msg.sender_name}</span>}
                      {msg.message_type === 'image' && msg.file_url ? (
                        <div className={`rounded-2xl overflow-hidden ${isMe ? 'rounded-br-sm' : 'rounded-bl-sm'}`} style={{ maxWidth: 220 }}>
                          <img src={msg.file_url?.startsWith("https://") ? msg.file_url : `https://qtalk.qualtechedge.in${msg.file_url}`} alt={msg.file_name} className="w-full h-auto" />
                        </div>
                      ) : msg.message_type === 'file' ? (
                        <a href={`https://qtalk.qualtechedge.in${msg.file_url}`} target="_blank" rel="noreferrer"
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-2xl ${isMe ? 'bg-teal-500 text-white rounded-br-sm' : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-bl-sm border border-gray-100 dark:border-gray-700'}`}>
                          <span className="text-xl">{getFileIcon(msg.file_mime_type)?.icon || '📎'}</span>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold truncate" style={{ maxWidth: 140 }}>{msg.file_name}</p>
                            <p className={`text-xs ${isMe ? 'text-teal-100' : 'text-gray-400'}`}>{formatFileSize(msg.file_size)}</p>
                          </div>
                        </a>
                      ) : (
                        <div className={`px-3 py-2 rounded-2xl text-sm ${isMe ? 'bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-br-sm' : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-bl-sm shadow-sm border border-gray-100 dark:border-gray-700'}`}>
                          {msg.is_deleted
                            ? <span className={`italic text-xs ${isMe ? 'text-teal-100' : 'text-gray-400'}`}>🚫 Message deleted</span>
                            : decryptMessage(msg.content)}
                        </div>
                      )}
                      <span className="text-[10px] text-gray-400 mt-0.5 px-1">{format(new Date(msg.created_at), 'HH:mm')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply preview */}
          {replyTo && (
            <div className="flex items-center gap-2 px-4 py-2 bg-teal-50 dark:bg-teal-900/20 border-t-2 border-teal-200">
              <div className="w-1 h-8 bg-teal-500 rounded-full flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-teal-600">↩ Replying to {replyTo.sender_name}</p>
                <p className="text-xs text-gray-500 truncate">{replyTo.content || '📎 Attachment'}</p>
              </div>
              <button onClick={() => setReplyTo(null)} className="text-gray-400 p-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            <div className="flex items-end gap-2">
              <button onClick={() => fileInputRef.current?.click()} className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 text-gray-500">
                {uploading
                  ? <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity=".3" /><path d="M12 2a10 10 0 0110 10" /></svg>
                  : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>
                }
              </button>
              <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" />
              <textarea value={messageInput} onChange={handleTyping} onKeyDown={handleKeyDown}
                placeholder="Message..." rows={1}
                className="flex-1 px-3 py-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 text-sm placeholder-gray-400 focus:outline-none focus:border-teal-400 resize-none"
                style={{ maxHeight: 100, overflowY: 'auto' }} />
              <button onClick={sendMessage} disabled={!messageInput.trim()}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-white flex-shrink-0 disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PEOPLE VIEW ── */}
      {view === 'people' && (
        <div className="flex flex-col h-full">
          <div className="px-4 pt-12 pb-3 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
            <h1 className="text-xl font-black text-gray-900 dark:text-white mb-3">People</h1>
            <div className="relative">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-2.5 w-4 h-4 text-gray-400">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input type="text" placeholder="Search people..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredUsers.map(u => (
              <div key={u.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-800">
                <Avatar name={u.full_name} url={u.avatar_url} size={11} online={isOnline(u.id)} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{u.full_name}</p>
                  <p className="text-xs text-gray-400">{u.designation || u.department || u.email}</p>
                </div>
                <button onClick={() => startChat(u.id)} className="w-8 h-8 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PROFILE VIEW ── */}
      {view === 'profile' && (
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Hero */}
          <div className="relative pt-14 pb-6 flex flex-col items-center" style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>
            <div className="w-20 h-20 rounded-2xl overflow-hidden border-4 border-white/30 shadow-xl mb-3">
              {user?.avatar_url?.startsWith('https://')
                ? <img src={user.avatar_url?.startsWith("https://") ? user.avatar_url : `https://qtalk.qualtechedge.in${user.avatar_url}`} className="w-full h-full object-cover" alt="" />
                : <div className="w-full h-full flex items-center justify-center text-3xl font-black text-white" style={{ background: 'rgba(255,255,255,0.2)' }}>{user?.full_name?.[0]?.toUpperCase()}</div>
              }
            </div>
            <h2 className="text-lg font-black text-white">{user?.full_name}</h2>
            <p className="text-white/60 text-xs mt-0.5">{user?.email}</p>
            <div className="flex gap-2 mt-2">
              {profileForm.designation && <span className="text-xs bg-white/15 text-white px-2 py-0.5 rounded-full border border-white/20">{profileForm.designation}</span>}
              {profileForm.department && <span className="text-xs bg-white/15 text-white px-2 py-0.5 rounded-full border border-white/20">{profileForm.department}</span>}
            </div>
          </div>

          <div className="p-4 space-y-3">
            {!editingProfile ? (
              <>
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 pt-3 pb-1">Contact Info</p>
                  {[
                    { label: 'Mobile', val: profileForm.mobile, icon: '📱' },
                    { label: 'Department', val: profileForm.department, icon: '🏢' },
                    { label: 'Designation', val: profileForm.designation, icon: '💼' },
                  ].map((item, i, arr) => (
                    <div key={item.label} className={`flex items-center gap-3 px-4 py-3 ${i < arr.length - 1 ? 'border-b border-gray-50 dark:border-gray-700' : ''}`}>
                      <span className="text-lg">{item.icon}</span>
                      <div>
                        <p className="text-xs text-gray-400">{item.label}</p>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{item.val || <span className="text-gray-400 italic font-normal">Not set</span>}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => setEditingProfile(true)}
                  className="w-full py-3 rounded-2xl border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center gap-2">
                  ✏️ Edit Profile
                </button>
                <button onClick={logout}
                  className="w-full py-3 rounded-2xl border border-red-100 dark:border-red-900/30 text-red-500 text-sm font-semibold flex items-center justify-center gap-2">
                  Sign out of Qtalk
                </button>
              </>
            ) : (
              <>
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  {[
                    { key: 'full_name', label: 'Full Name', type: 'text' },
                    { key: 'mobile', label: 'Mobile', type: 'tel' },
                    { key: 'department', label: 'Department', type: 'text' },
                    { key: 'designation', label: 'Designation', type: 'text' },
                    { key: 'status', label: 'Status', type: 'text' },
                  ].map((f, i, arr) => (
                    <div key={f.key} className={`px-4 py-3 ${i < arr.length - 1 ? 'border-b border-gray-50 dark:border-gray-700' : ''}`}>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-1">{f.label}</label>
                      <input type={f.type} value={profileForm[f.key]} onChange={e => setProfileForm({ ...profileForm, [f.key]: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl text-sm bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-transparent focus:border-teal-400 focus:outline-none" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingProfile(false)} className="flex-1 py-3 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-600">Cancel</button>
                  <button onClick={saveProfile} className="flex-1 py-3 rounded-2xl text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>Save</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── NOTIFICATIONS OVERLAY ── */}
      {showNotifs && (
        <div className="fixed inset-0 z-50 bg-black/40 flex flex-col justify-end" onClick={() => setShowNotifs(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-t-3xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="font-bold text-gray-900 dark:text-white">Notifications</h3>
              <button onClick={() => setShowNotifs(false)} className="text-gray-400 p-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-5 h-5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0
                ? <div className="flex flex-col items-center justify-center py-12 text-gray-400"><p className="text-sm">All caught up!</p></div>
                : notifications.map(n => (
                  <div key={n.id} onClick={() => {
                    const conv = conversations.find(c => c.id === n.conversation_id);
                    if (conv) { openConversation(conv); setShowNotifs(false); }
                  }} className="flex items-start gap-3 px-5 py-3.5 border-b border-gray-50 dark:border-gray-800 cursor-pointer active:bg-gray-50">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {n.title?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 dark:text-gray-100">{n.title}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{n.body}</p>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">{format(n.time, 'HH:mm')}</span>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* ── BOTTOM NAV (hidden during chat) ── */}
      {view !== 'chat' && (
        <div className="flex-shrink-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 flex items-center"
          style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>
          {[
            {
              id: 'chats', label: 'Chats', badge: totalUnread,
              icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
            },
            {
              id: 'people', label: 'People',
              icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
            },
            {
              id: 'profile', label: 'Profile',
              icon: user?.avatar_url?.startsWith('https://')
                ? <img src={user.avatar_url?.startsWith("https://") ? user.avatar_url : `https://qtalk.qualtechedge.in${user.avatar_url}`} className="w-6 h-6 rounded-full object-cover" alt="" />
                : <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg,#0d9488,#2563eb)' }}>{user?.full_name?.[0]?.toUpperCase()}</div>
            },
          ].map(tab => (
            <button key={tab.id} onClick={() => { setView(tab.id); setSearch(''); }}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 relative transition-all
                ${view === tab.id ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
              {tab.icon}
              <span className="text-[10px] font-semibold">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute top-1.5 right-[calc(50%-16px)] min-w-[16px] h-4 px-0.5 bg-red-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
              {view === tab.id && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-teal-500 rounded-full" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
